import { useMemo } from "react";

import {
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";

import { IPart } from "../../models/Part";
import { FormatType, IEbayAllLocations, IEbayAllPolicies, IEbayOffer } from "../../models/EbayOffer";
import StatusPill, { StatusPillColour } from "../shared/StatusPill";
import PriceFormat from "../../utils/priceFormat";
import checkEbayDisabled from "../../utils/checkEbayDisabled";
import usePartDetails from "../../data/usePartDetails";
import { IListingOptimisation } from "../../models/listingOptimisation/IListingOptimisation";

interface IOptimisationStagedCellProps {
  part: IPart;
  optimisation: IListingOptimisation | undefined;
  policies: IEbayAllPolicies | undefined;
  locations: IEbayAllLocations | undefined;
  onEdit: () => void;
}

function DiffRow({ label, from, to }: { label: string; from: string; to: string }) {
  return (
    <div className="flex items-start gap-1.5 text-xs">
      <span className="w-14 shrink-0 font-medium text-gray-500">{label}</span>
      <span className="min-w-0 truncate text-gray-500 line-through" title={from}>{from}</span>
      <ArrowRightIcon className="mt-0.5 h-3 w-3 shrink-0 text-gray-400" />
      <span className="min-w-0 truncate font-medium text-gray-900" title={to}>{to}</span>
    </div>
  );
}

// Applies the staged values on top of the live offer so the relist eligibility
// check runs against what would actually be published, not what's live now.
function mergeStagedOffer(offer: IEbayOffer, optimisation: IListingOptimisation | undefined): IEbayOffer {
  if (!optimisation) return offer;

  const merged: IEbayOffer = { ...offer };

  if (optimisation.newTitle != null) merged.title = optimisation.newTitle;

  if (optimisation.newPrice != null) {
    if (offer.format === FormatType.Auction) merged.auctionStartPrice = optimisation.newPrice;
    else merged.buyItNowPrice = optimisation.newPrice;
  }

  if (optimisation.newFulfillmentPolicyId != null) merged.fulfillmentPolicyId = optimisation.newFulfillmentPolicyId;
  if (optimisation.newPaymentPolicyId != null) merged.paymentPolicyId = optimisation.newPaymentPolicyId;
  if (optimisation.newReturnPolicyId != null) merged.returnPolicyId = optimisation.newReturnPolicyId;
  if (optimisation.newLocationKey != null) merged.locationKey = optimisation.newLocationKey;

  return merged;
}

// Shows what has been staged against a listing (vs the values live on eBay), any
// "can't relist" errors, and opens the staging editor. Nothing in this cell writes
// to the live listing.
export default function OptimisationStagedCell({ part, optimisation, policies, locations, onEdit }: IOptimisationStagedCellProps) {
  // The listings query returns slim rows - fetch full details (images/aspects)
  // when missing so the eligibility check can run. Same pattern as
  // ListingRecommendationsCell on the Active Listings table.
  const needsDetails = !Array.isArray(part.images) || !Array.isArray(part.aspects);
  const { partDetails } = usePartDetails(part.id, needsDetails);
  const fullPart = (needsDetails ? partDetails : null) ?? part;

  const offer = fullPart.offers?.at(0);
  const currentPrice =
    offer?.format === FormatType.Auction ? offer?.auctionStartPrice : offer?.buyItNowPrice;

  // Live = the listing as it is on eBay; effective = with staged changes applied.
  // Comparing the two lets us show when staged changes fix a blocking error.
  const { liveCheck, effectiveCheck } = useMemo(() => {
    if (!Array.isArray(fullPart.images) || !Array.isArray(fullPart.offers) || !offer) {
      return { liveCheck: null, effectiveCheck: null };
    }

    try {
      return {
        liveCheck: checkEbayDisabled({ part: fullPart, offer, ignoreListingTime: true }),
        effectiveCheck: checkEbayDisabled({
          part: fullPart,
          offer: mergeStagedOffer(offer, optimisation),
          ignoreListingTime: true,
        }),
      };
    } catch {
      return { liveCheck: null, effectiveCheck: null };
    }
  }, [fullPart, offer, optimisation]);

  const relistErrors = effectiveCheck?.errors?.length
    ? effectiveCheck.errors
    : effectiveCheck?.error
      ? [effectiveCheck.error]
      : [];
  const relistBlocked = !!effectiveCheck?.isDisabled && relistErrors.length > 0;
  const liveErrors = liveCheck?.errors?.length
    ? liveCheck.errors
    : liveCheck?.error
      ? [liveCheck.error]
      : [];
  const fixedByStagedChanges =
    !relistBlocked && !!liveCheck?.isDisabled && liveErrors.length > 0;

  const policyName = (policyId: string | null | undefined) => {
    if (!policyId) return "—";
    const all = [
      ...(policies?.fulfilmentPolicies ?? []),
      ...(policies?.paymentPolicies ?? []),
      ...(policies?.returnPolicies ?? []),
    ];
    return all.find((p) => p.id === policyId)?.name ?? policyId;
  };

  const locationName = (locationKey: string | null | undefined) => {
    if (!locationKey) return "—";
    return locations?.locations?.find((l) => l.id === locationKey)?.name ?? locationKey;
  };

  const hasStagedChanges =
    !!optimisation &&
    (optimisation.newTitle != null ||
      optimisation.newPrice != null ||
      optimisation.newFulfillmentPolicyId != null ||
      optimisation.newPaymentPolicyId != null ||
      optimisation.newReturnPolicyId != null ||
      optimisation.newLocationKey != null ||
      optimisation.applyImageRules ||
      optimisation.removeBackgroundFirstImage ||
      optimisation.applyCategoryDefaults);

  return (
    <div className="flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/80 px-3 py-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Staged Changes
        </span>
        <span className="flex items-center gap-1.5">
          {relistBlocked && <StatusPill text="Can't relist" colour={StatusPillColour.Red} />}
          {hasStagedChanges ? (
            <StatusPill text="Staged" colour={StatusPillColour.Blue} />
          ) : (
            <StatusPill text="No changes" colour={StatusPillColour.Neutral} />
          )}
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-1 px-3 py-2">
        {relistBlocked && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-red-800">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-xs font-semibold">Can&apos;t relist</span>
              <ul className="list-disc pl-3.5 text-[11px] text-red-700/90">
                {relistErrors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            </span>
          </div>
        )}

        {fixedByStagedChanges && (
          <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 px-2 py-1.5 text-green-800">
            <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-xs font-semibold">Fixed by staged changes</span>
              <ul className="list-disc pl-3.5 text-[11px] text-green-700/90">
                {liveErrors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            </span>
          </div>
        )}

        {hasStagedChanges ? (
          <>
            {optimisation!.newTitle != null && (
              <DiffRow label="Title" from={offer?.title ?? "—"} to={optimisation!.newTitle} />
            )}
            {optimisation!.newPrice != null && (
              <DiffRow label="Price" from={PriceFormat(currentPrice)} to={PriceFormat(optimisation!.newPrice)} />
            )}
            {optimisation!.newFulfillmentPolicyId != null && (
              <DiffRow label="Shipping" from={policyName(offer?.fulfillmentPolicyId)} to={policyName(optimisation!.newFulfillmentPolicyId)} />
            )}
            {optimisation!.newPaymentPolicyId != null && (
              <DiffRow label="Payment" from={policyName(offer?.paymentPolicyId)} to={policyName(optimisation!.newPaymentPolicyId)} />
            )}
            {optimisation!.newReturnPolicyId != null && (
              <DiffRow label="Returns" from={policyName(offer?.returnPolicyId)} to={policyName(optimisation!.newReturnPolicyId)} />
            )}
            {optimisation!.newLocationKey != null && (
              <DiffRow label="Location" from={locationName(offer?.locationKey)} to={locationName(optimisation!.newLocationKey)} />
            )}
            {optimisation!.applyImageRules && (
              <div className="flex items-center gap-1.5 text-xs">
                <span className="w-14 shrink-0 font-medium text-gray-500">Images</span>
                <span className="font-medium text-gray-900">Apply image rules on relist</span>
              </div>
            )}
            {optimisation!.removeBackgroundFirstImage && (
              <div className="flex items-center gap-1.5 text-xs">
                <span className="w-14 shrink-0 font-medium text-gray-500">Images</span>
                <span className="font-medium text-gray-900">Remove BG on 1st image on relist</span>
              </div>
            )}
            {optimisation!.applyCategoryDefaults && (
              <div className="flex items-center gap-1.5 text-xs">
                <span className="w-14 shrink-0 font-medium text-gray-500">Defaults</span>
                <span className="font-medium text-gray-900">Apply category defaults on relist</span>
              </div>
            )}
          </>
        ) : (
          !relistBlocked && (
            <p className="text-xs text-gray-500">
              Stage title, price or policy changes to apply when this listing is relisted.
            </p>
          )
        )}

        <button
          type="button"
          onClick={onEdit}
          className="mt-auto flex w-fit items-center gap-1.5 rounded-md border border-primary-200 bg-primary-50 px-2 py-1.5 text-left text-xs font-semibold text-primary-800 transition hover:bg-primary-100"
        >
          <PencilSquareIcon className="h-4 w-4 shrink-0" />
          {hasStagedChanges ? "Edit Staged Changes" : "Stage Changes"}
        </button>
      </div>
    </div>
  );
}
